import React from 'react';
import { useRouter } from 'next/router';
import { styled } from 'sensortower-components/src/utils/styled';
import Container, { ContainerProps } from '@mui/material/Container';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import ErrorBoundary from 'sensortower-components/src/base-components/ErrorBoundary';

import Filters from '../Filters';
import Pagination from '../Pagination';
import { useLocalizationContext } from '../LocalizationContext';
import { sidekick } from '../../utils/sidekick';
import { useJobOpenings } from './hooks/useJobOpenings';
import { getJobsBoardViewData, splitCamelCase } from './utils/helpers';
import { JobsBoardFilters } from './utils/types';

export interface JobsBoardProps {
  id?: string;
  sidekickLookup: any;
}

const ITEMS_PER_PAGE = 20;

export const JobsBoard = ({ id, sidekickLookup }: JobsBoardProps) => {
  const router = useRouter();
  const localization = useLocalizationContext();
  // The active filter is the URL query itself (Filters pushes shallow route changes).
  const { page = 1, slug: _slug, ...query } = router.query;
  const filter = query as JobsBoardFilters;

  const { data: jobData, isLoading, error } = useJobOpenings();

  const { items, total } = getJobsBoardViewData({
    jobsByDepartmentName: jobData?.jobsByDepartmentName ?? [],
    filter,
    page: Number(page),
    itemsPerPage: ITEMS_PER_PAGE,
    departments: jobData?.departments ?? [],
  });

  const hasActiveFilter = Object.values(filter).some(
    (v) => v !== undefined && v !== '' && (!Array.isArray(v) || v.length > 0),
  );

  return (
    <ErrorBoundary>
      <Root elevation={3} {...sidekick(sidekickLookup)} id={id}>
        <Typography
          variant="h2"
          color="primary"
          sx={{
            mb: [3, 0],
            fontSize: 44,
            textAlign: 'center',
          }}
        >
          {localization['careers.openPositionsLabel']?.shortTextValue ?? 'Open Positions'}
        </Typography>
        <Container>
          <FilterContainer>
            <Filters
              filters={[
                {
                  id: 'department',
                  items: jobData?.departments,
                  label: localization['careers.filtersDepartmentsLabel']?.shortTextValue ?? 'Departments',
                  variant: 'outlined',
                  multiple: true,
                },
                {
                  id: 'location',
                  items: jobData?.locations,
                  label: localization['careers.filtersLocationLabel']?.shortTextValue ?? 'Location',
                  variant: 'outlined',
                  multiple: true,
                },
              ]}
            />
          </FilterContainer>

          {isLoading ? (
            <StatusBox>
              <CircularProgress aria-label="Loading open positions" />
            </StatusBox>
          ) : error ? (
            <Typography variant="body1" color="error" sx={{ textAlign: 'center', py: 8 }}>
              {localization['careers.loadErrorLabel']?.shortTextValue ??
                'Unable to load open positions. Please try again later.'}
            </Typography>
          ) : hasActiveFilter && items.length === 0 ? (
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                textAlign: 'center',
                py: 8,
              }}
            >
              No jobs matching your filter
            </Typography>
          ) : (
            <>
              <ContainerTable aria-label="Job Openings">
                {items?.map((row) => (
                  <BoxContainer sx={{ mb: 2 }} key={row.id}>
                    <Department>{row.externalLabel}</Department>
                    {row.jobs.map((item) => (
                      <Link
                        href={item.applyLink}
                        key={item.id}
                        underline="none"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'block',
                        }}
                      >
                        <JobRow>
                          <JobPosition>
                            {item.title}
                            <Location>
                              {item.locationName}{' '}
                              {item.employmentType && <> &middot; {splitCamelCase(item.employmentType)}</>}
                              {item.workplaceType && <> &middot; {item.workplaceType}</>}
                            </Location>
                          </JobPosition>
                        </JobRow>
                      </Link>
                    ))}
                  </BoxContainer>
                ))}
              </ContainerTable>

              <TableFooterContainer>
                <FooterRow sx={{ py: [3, 4] }}>
                  <Pagination count={total} />
                </FooterRow>
              </TableFooterContainer>
              <MobileTableFooterContainer>
                <FooterRow sx={{ py: [3, 4] }}>
                  <Pagination count={total} />
                </FooterRow>
              </MobileTableFooterContainer>
            </>
          )}
        </Container>
      </Root>
    </ErrorBoundary>
  );
};

const Root = styled(Container)<ContainerProps<React.ElementType>>(({ theme }) => ({
  width: '100%',
  alignSelf: 'stretch',
  marginBottom: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    padding: 0,
  },
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(6),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0),
  },
}));

const ContainerTable = styled('section')(({ theme }) => ({
  paddingBottom: theme.spacing(5),
  [theme.breakpoints.down('md')]: {
    paddingBottom: theme.spacing(2),
  },
}));

const BoxContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(2),
  },
}));

const Department = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontWeight: 700,
  lineHeight: '32px',
  fontSize: 18,
  color: theme.palette.slate.dark,
  display: 'block',
  overflowWrap: 'break-word',
  [theme.breakpoints.down('md')]: {
    fontSize: 16,
    lineHeight: '26px',
  },
}));

const JobRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(0, -2),
  padding: theme.spacing(0, 2),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    color: 'secondary.contrastText',
    [theme.breakpoints.down('md')]: {
      backgroundColor: 'unset',
      color: 'unset',
    },
  },
}));

const JobPosition = styled('div')(({ theme }) => ({
  fontWeight: 600,
  fontSize: 16,
  lineHeight: '32px',
  color: theme.palette.teal.main,
  cursor: 'pointer',
  width: '100%',
  padding: '16px 0px',
  [theme.breakpoints.down('md')]: {
    fontSize: 12,
    lineHeight: '28px',
    padding: theme.spacing(1.5, 0),
  },
}));

const Location = styled(Typography)(({ theme }) => ({
  color: theme.palette.slate.main,
  fontSize: 16,
  lineHeight: '24px',
  [theme.breakpoints.down('md')]: {
    fontSize: 12,
  },
}));

const TableFooterContainer = styled(Box)(({ theme }) => ({
  display: 'block',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const MobileTableFooterContainer = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('md')]: {
    display: 'block',
  },
}));

const FooterRow = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const StatusBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(8, 0),
}));

export default JobsBoard;
